import { ReactNode } from "react";
import { Box, Button, Flex, List, ListItem } from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import {
  ArrayPath,
  FieldArray,
  FieldValues,
  useFieldArray,
} from "react-hook-form";
import type { BaseFilterFormProps } from "./utils";
import Filter from "./Filter";

type Props<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>,
> = BaseFilterFormProps<TFieldValues, TName> & {
  footerRight?: ReactNode;
  onClear?: () => void;
};

export default function FilterEditor<
  TFieldValues extends FieldValues,
  TName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>,
>({
  filterDefinitions,
  filtersFormName,
  footerRight,
  formContext,
  onClear,
}: Props<TFieldValues, TName>) {
  const {
    fields: filterFields,
    append,
    remove,
  } = useFieldArray({
    control: formContext.control,
    name: filtersFormName,
  });
  return (
    <Box>
      <List minH="3.75em" p={1} m={-1} overflowX="auto">
        {filterFields?.map((filterField, index) => (
          <Filter
            key={filterField.id}
            {...{ filterDefinitions, filtersFormName, formContext, index }}
            onRemove={remove}
          />
        ))}
        {!filterFields?.length && (
          <ListItem fontSize="xs" textAlign="center" color="gray" p={5} pb={0}>
            No filters have been added.
          </ListItem>
        )}
      </List>

      <Flex alignItems="center" mt={7}>
        <Button
          variant="outline"
          leftIcon={<AddIcon />}
          size="xs"
          colorScheme="pinkButton"
          onClick={() =>
            append({ filter: undefined } as FieldArray<TFieldValues, TName>)
          }
        >
          Add Filter
        </Button>

        <Box ml="auto">
          <Button
            variant="ghost"
            size="xs"
            color="gray.500"
            mr={2}
            _hover={{ bg: "gray.200" }}
            onClick={() => {
              remove();
              onClear?.();
            }}
          >
            Clear Filters
          </Button>
          {footerRight}
        </Box>
      </Flex>
    </Box>
  );
}
